import { useEffect, useState, useRef, useMemo, useContext } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { isEmpty, isEqual, debounce, cloneDeep } from 'lodash'

import {
	DataGridPremium,
	GridToolbarContainer,
	GridToolbarFilterButton,
	gridPaginatedVisibleSortedGridRowEntriesSelector,
} from '@mui/x-data-grid-premium'
import { makeStyles } from '@mui/styles'
import { IconThemeContext } from '../../../../custom-components/context/IconThemesContext'

import ExportButton from '../../../data/datagrid/ExportButton'

import { useTileDashboard } from '../../dis/dashboard/Dashboard'
import { dayjsToMoment, isDayjsObject } from 'utils/functions/doformsDateUtil'

import {
	CustomPagination,
	PAGE_SIZE_LIST,
	DEFAULT_PAGE_SIZE,
} from '../../../../components/data/datagrid/DatagridCustomComponent/CustomPagination'

const useStyles = makeStyles(() => ({}))

export const TOOLBAR_ITEMS = {
	EXPORT: 'export',
	DISPATCH: 'dispatch',
	FILTER: 'filter',
}

export function TileDatagrid(props) {
	const {
		dataGridTileStyle = {},
		tile,
		columns,
		rows,
		isLoading,
		apiRef,
		printTitle,
		toolbarItems,
		initGridColumnVisibilityModel,
	} = props

	const [t] = useTranslation('common')

	const { onSelectRow, onSelectCurrent, removeSelectedRowOfTile } = useTileDashboard()

	// grid models
	const [selectionModel, setSelectionModel] = useState([])
	const [gridFilterModel, setGridFilterModel] = useState({ items: [] })
	const [gridRowGroupingModel, setGridRowGroupingModel] = useState([])
	const [gridSortModel, setGridSortModel] = useState([])
	const [gridAggregationModel, setGridAggregationModel] = useState({})
	const [gridColumnVisibilityModel, setGridColumnVisibilityModel] = useState({})

	useEffect(() => {
		if (isEqual(initGridColumnVisibilityModel, gridColumnVisibilityModel)) return

		setGridColumnVisibilityModel(initGridColumnVisibilityModel)
	}, [initGridColumnVisibilityModel])

	const handleColumnVisibilityModelChange = (newModel) => {
		setGridColumnVisibilityModel(newModel)
	}

	useEffect(() => {
		removeSelectedRowOfTile(tile?.key)
		setSelectionModel([])
	}, [rows])

	return (
		<DataGridPremium
			sx={{
				'& .MuiDataGrid-footerContainer': {
					minHeight: '40px !important',
				},
				...dataGridTileStyle,
			}}
			localeText={{
				// Export selector toolbar button text
				toolbarExport: t('common:misc.export'),
				toolbarExportLabel: t('common:misc.exportLabel'),
				toolbarExportCSV: t('common:misc.exportCSV'),
				toolbarExportPrint: t('common:misc.exportPrint'),
				toolbarExportExcel: t('common:misc.exportExcel'),
			}}
			columns={columns}
			rows={rows}
			rowSelectionModel={selectionModel}
			onRowClick={(params) => {
				const selectedRowData = rows.filter((row) => row.id === params.id)

				onSelectRow(tile?.key, selectedRowData?.[0])
				setSelectionModel((prevModel) => {
					if (prevModel[0] === params.id) {
						return []
					}
					return [params.id]
				})
				onSelectCurrent(tile?.i)
			}}
			loading={isLoading}
			apiRef={apiRef}
			defaultGroupingExpansionDepth={-1}
			rowHeight={25}
			columnHeaderHeight={28}
			getRowId={(row) => row.id}
			columnVisibilityModel={gridColumnVisibilityModel}
			onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
			filterModel={gridFilterModel}
			onFilterModelChange={setGridFilterModel}
			aggregationModel={gridAggregationModel}
			onAggregationModelChange={setGridAggregationModel}
			rowGroupingModel={gridRowGroupingModel}
			onRowGroupingModelChange={setGridRowGroupingModel}
			sortModel={gridSortModel}
			onSortModelChange={setGridSortModel}
			slots={{
				toolbar: isEmpty(toolbarItems) ? null : CustomToolbar,
			}}
			slotProps={{
				toolbar: {
					toolbarItems,
					printTitle,
					apiRef,
					rows,
					columns,
					aggregationModel: gridAggregationModel,
					rowGroupingModel: gridRowGroupingModel,
					filterModel: gridFilterModel,
					sortModel: gridSortModel,
				},
			}}
		/>
	)
}

const CustomToolbar = (props) => {
	const {
		toolbarItems,
		printTitle,
		apiRef,
		rows,
		columns,
		aggregationModel,
		rowGroupingModel,
		filterModel,
		sortModel,
	} = props
	const { environment } = useSelector((state) => state)
	return (
		<GridToolbarContainer
			sx={{
				'& .MuiButton-root': {
					color: environment.iconsTheme[0],
					':hover': {
						color: environment.iconsTheme[1],
						backgroundColor: 'transparent',
					},
				},
				padding: '0 5px',
			}}
		>
			{toolbarItems.includes(TOOLBAR_ITEMS.EXPORT) && (
				<ExportButton
					printTitle={printTitle}
					sx={environment.iconsTheme}
					viewData={null}
					gridRows={rows}
					gridColumns={columns}
					records={rows}
					aggregationModel={aggregationModel}
					isGrouping={rowGroupingModel.length > 0}
					formattedRowsWithNumbers={rows}
					formatGridColumns={columns}
					rowGroupingModel={rowGroupingModel}
					sortModel={sortModel}
					filterModel={filterModel}
					getVisibleRows={() => {
						const paginationRowModels = gridPaginatedVisibleSortedGridRowEntriesSelector(apiRef)
						let rs = []
						paginationRowModels.forEach((rowModel, index) => {
							const a = rowModel.model
							// a.id = index + 1
							rs.push(a)
						})
						return rs
					}}
				/>
			)}
			{toolbarItems.includes(TOOLBAR_ITEMS.FILTER) && (
				<GridToolbarFilterButton sx={environment.iconsTheme} />
			)}
		</GridToolbarContainer>
	)
}

export function TilePaginationDatagrid(props) {
	const {
		datagridStyle = {},
		tile,
		columns,
		rows,
		isLoading,
		apiRef,
		printTitle,
		toolbarItems,
		initGridFilterModel = { items: [] },
		handleSelectRow,
		onCellClick,
		initGridColumnVisibilityModel,
		useTileDashboardContext = true,
	} = props

	const [t] = useTranslation('common')

	const { onSelectRow, onSelectCurrent, removeSelectedRowOfTile } = useTileDashboard()

	// grid models
	const [selectionModel, setSelectionModel] = useState([])
	const [gridFilterModel, setGridFilterModel] = useState({ items: [] })
	const [gridRowGroupingModel, setGridRowGroupingModel] = useState([])
	const [gridSortModel, setGridSortModel] = useState([])
	const [gridAggregationModel, setGridAggregationModel] = useState({})
	const [gridColumnVisibilityModel, setGridColumnVisibilityModel] = useState({})

	useEffect(() => {
		if (isEqual(initGridFilterModel, gridFilterModel)) return

		setGridFilterModel(initGridFilterModel)
	}, [])

	useEffect(() => {
		if (isEqual(initGridColumnVisibilityModel, gridColumnVisibilityModel)) return

		setGridColumnVisibilityModel(initGridColumnVisibilityModel)
	}, [initGridColumnVisibilityModel])

	const handleColumnVisibilityModelChange = (newModel) => {
		setGridColumnVisibilityModel(newModel)
	}

	const handleFilterModelChange = debounce((nextFilterModel, detail) => {
		// Check if user clicked on filter icon, don't do anything
		if (!detail?.reason) {
			setGridFilterModel(nextFilterModel)
			return
		}

		// if (detail?.reason === 'removeAllFilterItems') {
		// 	if (!isEmpty(dataFilters.serverFilters) || !isEmpty(dataFilters.linkedFilters)) {
		// 		setAlertInfo((prev) => ({
		// 			...prev,
		// 			open: true,
		// 			type: 'error',
		// 			text: "You don't have permission to change filter",
		// 		}))
		// 		return
		// 	}

		// 	onHeaderFilterModel('filterModel', [])
		// 	setGridFilterModel(nextFilterModel)
		// 	return
		// }

		// if (detail?.reason === 'changeLogicOperator') {
		// 	if (!isEmpty(dataFilters.serverFilters) || !isEmpty(dataFilters.linkedFilters)) {
		// 		setAlertInfo((prev) => ({
		// 			...prev,
		// 			open: true,
		// 			type: 'error',
		// 			text: "You don't have permission to change filter",
		// 		}))
		// 		return
		// 	}
		// }

		// const nextServerFilters = nextFilterModel.items.filter(
		// 	(item) => DATA_FILTERS_REDUCER.SERVER === item.filterType
		// )
		// const nextLinkedFilters = nextFilterModel.items.filter(
		// 	(item) => DATA_FILTERS_REDUCER.LINKED_FIELDS === item.filterType
		// )

		// const isRemovingStableFilter =
		// 	nextServerFilters.length !== dataFilters.serverFilters.length ||
		// 	nextLinkedFilters.length !== dataFilters.linkedFilters.length
		// const isEditingServerFilters = !isEqual(
		// 	dataFilters.serverFilters.map((item) => ({
		// 		value: item.value,
		// 		operator: item.operator,
		// 		field: item.field,
		// 	})),
		// 	nextServerFilters.map((item) => ({
		// 		value: item.value,
		// 		operator: item.operator,
		// 		field: item.field,
		// 	}))
		// )

		// const isEditingLinkedFilters = !isEqual(
		// 	dataFilters.linkedFilters.map((item) => ({
		// 		value: item.value,
		// 		operator: item.operator,
		// 		field: item.field,
		// 	})),
		// 	nextLinkedFilters.map((item) => ({
		// 		value: item.value,
		// 		operator: item.operator,
		// 		field: item.field,
		// 	}))
		// )

		// const isEditingStableFilter = isEditingServerFilters || isEditingLinkedFilters
		// const isCustomizedStableFilter = isRemovingStableFilter || isEditingStableFilter

		const computedValueItems = nextFilterModel.items.map((item) => {
			const isDayjsValue = isDayjsObject(item.value)
			if (!isDayjsValue) return item

			const momentObj = dayjsToMoment(item.value)
			return { ...item, value: momentObj.toISOString() }
		})

		const computedNextFilterModel = cloneDeep(nextFilterModel)
		computedNextFilterModel.items = computedValueItems
		// onHeaderFilterModel('filterModel', nextClientFilters)
		setGridFilterModel(computedNextFilterModel)
	}, 500)

	const [paginationModel, setPaginationModel] = useState({
		page: 0,
		pageSize: DEFAULT_PAGE_SIZE,
	})

	useEffect(() => {
		removeSelectedRowOfTile(tile?.key)
		setSelectionModel([])
	}, [rows])

	return (
		<DataGridPremium
			sx={{
				'& .MuiDataGrid-footerContainer': {
					minHeight: '40px !important',
				},
				'& .MuiTablePagination-selectLabel': {
					marginTop: '14px',
					marginBottom: '14px',
				},
				'& .MuiTablePagination-displayedRows': {
					marginTop: '14px',
					marginBottom: '14px',
				},
				...datagridStyle,
			}}
			localeText={{
				// Export selector toolbar button text
				toolbarExport: t('common:misc.export'),
				toolbarExportLabel: t('common:misc.exportLabel'),
				toolbarExportCSV: t('common:misc.exportCSV'),
				toolbarExportPrint: t('common:misc.exportPrint'),
				toolbarExportExcel: t('common:misc.exportExcel'),
			}}
			columns={columns}
			rows={rows}
			rowSelectionModel={selectionModel}
			onRowClick={(params) => {
				const selectedRowData = rows.filter((row) => row.id === params.id)

				setSelectionModel((prevModel) => {
					if (prevModel[0] === params.id) {
						return []
					}
					return [params.id]
				})

				if (useTileDashboardContext) {
					onSelectRow(tile?.key, selectedRowData?.[0])
					onSelectCurrent(tile?.i)
				}

				if (handleSelectRow) {
					handleSelectRow(params, 'view', rows)
				}
			}}
			loading={isLoading}
			apiRef={apiRef}
			defaultGroupingExpansionDepth={-1}
			rowHeight={25}
			columnHeaderHeight={28}
			getRowId={(row) => row.id}
			columnVisibilityModel={gridColumnVisibilityModel}
			onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
			filterModel={gridFilterModel}
			onFilterModelChange={handleFilterModelChange}
			aggregationModel={gridAggregationModel}
			onAggregationModelChange={setGridAggregationModel}
			rowGroupingModel={gridRowGroupingModel}
			onRowGroupingModelChange={setGridRowGroupingModel}
			sortModel={gridSortModel}
			onSortModelChange={setGridSortModel}
			pagination
			paginationModel={paginationModel}
			onPaginationModelChange={setPaginationModel}
			slots={{
				toolbar: isEmpty(toolbarItems) ? null : CustomToolbar,
			}}
			slotProps={{
				toolbar: {
					toolbarItems,
					printTitle,
					apiRef,
					rows,
					columns,
					aggregationModel: gridAggregationModel,
					rowGroupingModel: gridRowGroupingModel,
					filterModel: gridFilterModel,
					sortModel: gridSortModel,
				},
			}}
			onCellClick={onCellClick}
		/>
	)
}

export function ControlledTileDatagrid(props) {
	const {
		datagridStyle = {},
		tile,
		columns,
		rows,
		isLoading,
		apiRef,
		printTitle,
		toolbarItems,

		// FULLY CONTROLLED PROPS FROM PARENT
		gridFilterModel,
		onGridFilterModelChange,
		gridRowGroupingModel,
		onGridRowGroupingModelChange,
		gridSortModel,
		onGridSortModelChange,
		gridAggregationModel,
		onGridAggregationModelChange,
		gridColumnVisibilityModel,
		onGridColumnVisibilityModelChange,
		paginationModel,
		onPaginationModelChange,
		columnsWidth,
		onGridColumnResize,
		columnsOrder,
		onGridColumnOrderChange,

		handleSelectRow,
		onCellClick,
		useTileDashboardContext = true,
	} = props

	const [t] = useTranslation('common')
	const { onSelectRow, onSelectCurrent, removeSelectedRowOfTile } = useTileDashboard()

	/** ROW SELECTION — can also be parent-controlled, but usually local is ok */
	const [selectionModel, setSelectionModel] = useState([])

	useEffect(() => {
		removeSelectedRowOfTile(tile?.key)
		setSelectionModel([])
	}, [rows])

	const handleRowClick = (params) => {
		const selectedRowData = rows.filter((row) => row.id === params.id)

		setSelectionModel((prevModel) => {
			if (prevModel[0] === params.id) {
				return []
			}
			return [params.id]
		})

		if (useTileDashboardContext) {
			onSelectRow(tile?.key, selectedRowData?.[0])
			onSelectCurrent(tile?.i)
		}

		if (handleSelectRow) {
			handleSelectRow(params, 'view', rows)
		}
	}

	const resizedGridColumns = useMemo(() => {
		if (isEmpty(columnsWidth)) return columns

		const resizedColumns = columns.map((column) => {
			if (!column.field) return column
			const width = columnsWidth[column.field]
			if (!!width) {
				column.width = width
			}

			return column
		})

		if (!isEmpty(columnsOrder)) {
			const ordered = columnsOrder
				.map((name) => resizedColumns.find((col) => col.field === name))
				.filter(Boolean)

			return ordered
		}

		return resizedColumns
	}, [columns, columnsWidth, columnsOrder])

	const debounceOnColumnResize = debounce((params, event, details) => {
		const colsWidth = cloneDeep(columnsWidth)
		colsWidth[params.colDef.field] = params.colDef.width
		onGridColumnResize(colsWidth)
	}, 1000)

	return (
		<DataGridPremium
			sx={{
				'& .MuiDataGrid-footerContainer': { minHeight: '40px !important' },
				'& .MuiTablePagination-selectLabel': { mt: '14px', mb: '14px' },
				'& .MuiTablePagination-displayedRows': { mt: '14px', mb: '14px' },
				...datagridStyle,
			}}
			localeText={{
				toolbarExport: t('common:misc.export'),
				toolbarExportLabel: t('common:misc.exportLabel'),
				toolbarExportCSV: t('common:misc.exportCSV'),
				toolbarExportPrint: t('common:misc.exportPrint'),
				toolbarExportExcel: t('common:misc.exportExcel'),
			}}
			rows={rows}
			columns={resizedGridColumns}
			onColumnOrderChange={(params, event, detail) => {
				const { column, oldIndex, targetIndex } = params

				// Extract current order
				const order = resizedGridColumns.map((col) => col.field)

				// Remove the moved column
				order.splice(oldIndex, 1)

				// Insert at new index
				order.splice(targetIndex, 0, column.field)

				onGridColumnOrderChange(order)
			}}
			loading={isLoading}
			apiRef={apiRef}
			filterModel={gridFilterModel}
			onFilterModelChange={onGridFilterModelChange}
			rowGroupingModel={gridRowGroupingModel}
			onRowGroupingModelChange={onGridRowGroupingModelChange}
			sortModel={gridSortModel}
			onSortModelChange={onGridSortModelChange}
			aggregationModel={gridAggregationModel}
			onAggregationModelChange={onGridAggregationModelChange}
			columnVisibilityModel={gridColumnVisibilityModel}
			onColumnVisibilityModelChange={onGridColumnVisibilityModelChange}
			paginationModel={paginationModel}
			onPaginationModelChange={onPaginationModelChange}
			onColumnResize={debounceOnColumnResize}
			rowSelectionModel={selectionModel}
			onRowClick={handleRowClick}
			getRowId={(r) => r.id}
			rowHeight={25}
			columnHeaderHeight={28}
			defaultGroupingExpansionDepth={-1}
			onCellClick={onCellClick}
			pagination
			slots={{
				toolbar: isEmpty(toolbarItems) ? null : CustomToolbar,
			}}
			slotProps={{
				toolbar: {
					toolbarItems,
					printTitle,
					apiRef,
					rows,
					columns,
					aggregationModel: gridAggregationModel,
					rowGroupingModel: gridRowGroupingModel,
					filterModel: gridFilterModel,
					sortModel: gridSortModel,
				},
			}}
		/>
	)
}
